import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {LoggedInCallback} from "../../service-auth/cognito.service";
import {EcommerceConnection} from "../../../model/EcommerceConnection";
import {RoleChangeListener, StartupService} from "../../service-ui/startup.service";
import { MatSidenav } from "@angular/material/sidenav";
import {MatDialog} from "@angular/material/dialog";
import {MediaMatcher} from "@angular/cdk/layout";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {EcommerceConnectionService} from "../../service-data/ecommerce-connection.service";
import {OrganizationService} from "../../service-data/organization.service";
import {ConnectShopifyDialogComponent} from "./connect-shopify-dialog/connect-shopify-dialog.component";
import {modernDialogConfig} from "../../service-ui/dialog-config";

@Component({
  selector: 'app-ecommerce',
  templateUrl: './ecommerce.component.html',
  styleUrls: ['./ecommerce.component.css']
})


export class EcommerceComponent implements OnDestroy, OnInit, LoggedInCallback, AfterViewInit, RoleChangeListener {
  @ViewChild('mainSideNav', { static: true }) mainSideNav: MatSidenav;
  mobileQuery: MediaQueryList;
  filterCollapseQuery: MediaQueryList;
  private _mobileQueryListener: () => void;
  private _filterCollapseListener: () => void;
  onDestroySubject: Subject<boolean> = new Subject();

  selectedEcommerceConnection: EcommerceConnection;
  selectedEcommerceConnectionId: string;
  editEcommerceConnection: EcommerceConnection;
  ecommerceConnections: EcommerceConnection[] = [];
  selectedTypeFilter = 'ALL';
  useFilterSelect = false;

  /**
   * Optional matrix parameter (e.g. arriving via
   * `Router.navigate(['/secure/ecommerce', { ecommerceConnectionId: 123 }])`).
   * When set, the component prefers this connection over the
   * default "first non-CSV" selection as soon as the matching
   * connection arrives from the live data feed. Cleared after
   * the corresponding selection happens so subsequent role
   * changes fall back to the default selection logic.
   */
  private requestedEcommerceConnectionId: string | null = null;

  /**
   * Live-data subscriptions are wired exactly once.
   *
   * `getData()` runs three times on every mount (`ngOnInit`,
   * `isLoggedIn()` -- which `isAuthenticated()` calls synchronously once
   * the session is READY -- and `ngAfterViewInit`). Re-subscribing on
   * each of those left three handlers on the same subject for the life
   * of the component, which turned one server-side add into three calls
   * to `addUpdate()`.
   */
  private liveDataSubscribed = false;

  get distinctTypes(): string[] {
    const types = new Set<string>();
    for (const ec of this.ecommerceConnections) {
      if (ec.type) {
        types.add(ec.type);
      }
    }
    return Array.from(types).sort();
  }

  get filteredConnections(): EcommerceConnection[] {
    if (this.selectedTypeFilter === 'ALL') {
      return this.ecommerceConnections;
    }
    return this.ecommerceConnections.filter(ec => ec.type === this.selectedTypeFilter);
  }

  constructor(public router: Router, public startupService: StartupService, public changeDetectorRef: ChangeDetectorRef, public media: MediaMatcher, private ecommerceConnectionService: EcommerceConnectionService, public organizationService: OrganizationService, private activatedRoute: ActivatedRoute, public dialog: MatDialog) {
    this.startupService.addRoleChangeListener(this);
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
    this.filterCollapseQuery = media.matchMedia('(max-width: 768px)');
    this.useFilterSelect = this.filterCollapseQuery.matches;
    this._filterCollapseListener = () => {
      this.useFilterSelect = this.filterCollapseQuery.matches;
      changeDetectorRef.detectChanges();
    };
    this.filterCollapseQuery.addListener(this._filterCollapseListener);
  }

  roleChanged(role: string, roleId: string): void {
    this.getData();
  }

  ngOnInit() {
    this.startupService.isAuthenticated(this);
    // Honor an inbound matrix parameter like
    //   /secure/ecommerce;ecommerceConnectionId=123
    // (used by the Shopify install handoff in AppComponent — see
    // docs/architecture/routing.md). The actual selection happens
    // in addUpdate() once the matching EcommerceConnection arrives
    // from the live data feed.
    const requested = this.activatedRoute.snapshot.paramMap.get('ecommerceConnectionId');
    if (requested) {
      this.requestedEcommerceConnectionId = requested;
    }
    this.getData();
  }

  ngAfterViewInit(): void {
    this.getData();
  }

  isLoggedIn(message: string, isLoggedIn: boolean) {
    if (!isLoggedIn) {
      this.router.navigate(['/home/login']);
    } else {
      this.getData();
    }
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
    this.filterCollapseQuery.removeListener(this._filterCollapseListener);
    this.onDestroySubject.next(true);
    this.onDestroySubject.unsubscribe();
  }

  getData(): void {
    this.startupService.touchLastUserActivity();

    // Subscribe BEFORE anything else, and WITHOUT the flood.
    //
    // `AbstractLiveDataService.getAddNotifier(flood)` replays the whole
    // cache into the subject and only THEN returns the subject, so the
    // caller that asked for the flood is the one subscriber guaranteed
    // to miss it. This list used to fill up anyway, but only by
    // coincidence: `getData()` runs three times per mount, so the
    // *second* call's flood reached the *first* call's subscription.
    // That coincidence is what broke in the `network` role -- there the
    // EcommerceConnections are still being paged in when those early
    // calls happen, so the replay carried one connection (or none), the
    // last `this.ecommerceConnections = []` wiped whatever had arrived,
    // and the store `<select>` -- which only renders when
    // `ecommerceConnections.length > 1` -- never appeared again.
    //
    // Seeding from `ecommerceConnectionService.objects` below makes the
    // list a function of the cache rather than of call ordering.
    if ( !this.liveDataSubscribed ) {
      this.liveDataSubscribed = true;
      this.ecommerceConnectionService.getAddNotifier(false).pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
        this.addUpdate(o);
      });
      this.ecommerceConnectionService.getUpdateNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
        this.addUpdate(o);
      });
      this.ecommerceConnectionService.getDeleteNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
        this.removeConnection(o);
      });
    }

    this.seedFromServiceCache();
    this.ensureConnectionsLoaded();
  }

  /**
   * Rebuild the list from everything the service currently holds.
   *
   * Safe to call repeatedly: it preserves the current selection when
   * that connection is still present, so a late `getData()` cannot
   * bounce the operator back to the first store while they are editing
   * a different one.
   */
  private seedFromServiceCache(): void {
    const previouslySelectedId = this.selectedEcommerceConnectionId;
    this.ecommerceConnections = [];
    this.selectedEcommerceConnection = undefined;
    this.selectedEcommerceConnectionId = undefined;
    for ( const o of this.ecommerceConnectionService.objects ) {
      this.addUpdate(o);
    }
    if ( previouslySelectedId ) {
      const previous = this.ecommerceConnections.find(c => '' + c.ID === previouslySelectedId);
      if ( previous ) {
        this.selectConnection(previous);
      }
    }
  }

  /**
   * Ask the server for the connections when nobody has yet.
   *
   * `StartupComponent` loads them for the `network` and `organization`
   * roles, but this screen is reachable without that load having
   * happened or having succeeded (deep link, a role whose startup path
   * skips the entity, an errored load). Previously the screen simply
   * rendered the "connect your first store" empty state in that case,
   * which is an actively misleading thing to show an operator who has
   * stores.
   *
   * `reset: false` is deliberate -- the cache is shared app-wide and
   * `reset: true` would wipe rows other screens are using (see
   * docs/architecture/live-data-service-caching.md). With
   * `loadingState === 'INITIAL'` the `false` still issues the request.
   * The result is NOT assigned to a field, for the same reason.
   */
  private ensureConnectionsLoaded(): void {
    const loadingState = this.ecommerceConnectionService.loadingState;
    if ( loadingState !== 'INITIAL' && loadingState !== 'ERROR' ) {
      return;
    }
    const ecommerceConnectionsFilter = {
      eq: {
        ' ecommerceConnection.state = "ACTIVE" AND ecommerceConnection.type in ("SHOPIFY", "WOOCOMMERCE", "CSV") ': 1
      }
    };
    this.ecommerceConnectionService.getFilteredItems(ecommerceConnectionsFilter, false, 25)
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(() => {
        this.seedFromServiceCache();
      });
  }

  contains(object: EcommerceConnection, array: EcommerceConnection[] ): boolean {
    for ( const o of array ) {
      if ( o.getId() === object.getId() ) {
        return true;
      }
    }
    return false;
  }

  addUpdate(o: EcommerceConnection) {
    try {
      if ( this.match(o) ) {
        if ( this.ecommerceConnections.findIndex(item => item.getId() === o.getId()) === -1) {
          this.ecommerceConnections.push(o);
          this.sortConnections();
        }
      }
    } catch ( e ) {
      console.log(`ERROR in EcommerceConnectionComponent addUpdate ${o.getId()} ${e.message} ${this.startupService.selectedRole}:${this.startupService.selectedRoleId}`);
    }
    if (this.ecommerceConnections.length > 0) {
      // If the caller asked for a specific connection (e.g. via
      // the Shopify install handoff), select it as soon as it
      // arrives — overriding both the initial default and any
      // earlier auto-selection that happened before this one
      // showed up in the feed.
      if (this.requestedEcommerceConnectionId) {
        const requested = this.ecommerceConnections.find(c => '' + c.ID === this.requestedEcommerceConnectionId);
        if (requested) {
          this.selectConnection(requested);
          this.requestedEcommerceConnectionId = null;
          return;
        }
      }
      if (!this.selectedEcommerceConnection) {
        const nonCsv = this.ecommerceConnections.find(ec => ec.type !== 'CSV');
        this.selectConnection(nonCsv || this.ecommerceConnections[0]);
      } else if (this.selectedEcommerceConnection.type === 'CSV' && o.type !== 'CSV') {
        this.selectConnection(o);
      }
    }
  }

  /**
   * Drop a connection the server says is gone.
   *
   * The delete notifier used to be subscribed to and then ignored,
   * which left a deleted store in the picker until the next full
   * reload -- and selectable, so the settings pane could be editing a
   * row that no longer exists.
   */
  removeConnection(o: EcommerceConnection): void {
    if ( !o ) {
      return;
    }
    const index = this.ecommerceConnections.findIndex(item => item.getId() === o.getId());
    if ( index === -1 ) {
      return;
    }
    this.ecommerceConnections.splice(index, 1);
    if ( this.selectedEcommerceConnection && this.selectedEcommerceConnection.getId() === o.getId() ) {
      this.selectedEcommerceConnection = undefined;
      this.selectedEcommerceConnectionId = undefined;
      const filtered = this.filteredConnections;
      if ( filtered.length > 0 ) {
        this.selectConnection(filtered[0]);
      }
    }
  }

  /**
   * Stable ordering for the picker.
   *
   * Arrival order is whatever order the parallel page requests came
   * back in, which is not stable between loads. In the `network` role
   * the list spans every organization, so grouping by organization and
   * then by type is the only way the operator can find a store twice.
   */
  private sortConnections(): void {
    this.ecommerceConnections.sort((a, b) => {
      const aOrg = this.getOrganizationName(a);
      const bOrg = this.getOrganizationName(b);
      if ( aOrg !== bOrg ) {
        return aOrg.localeCompare(bOrg);
      }
      const aType = a.type || '';
      const bType = b.type || '';
      if ( aType !== bType ) {
        return aType.localeCompare(bType);
      }
      // Within one organization and one type there can still be several
      // stores. Ordering those by id orders them by the accident of when each
      // was connected; ordering by the store identifier puts the list in the
      // order the merchant would write it down.
      const aStore = a.storeLabel || '';
      const bStore = b.storeLabel || '';
      if ( aStore !== bStore ) {
        return aStore.localeCompare(bStore);
      }
      return Number(a.ID) - Number(b.ID);
    });
  }

  getOrganizationName(ec: EcommerceConnection): string {
    const organization = this.organizationService && this.organizationService.objectsById
      ? this.organizationService.objectsById['' + ec.org_ID]
      : undefined;
    return organization && organization.name ? organization.name : 'Org ' + ec.org_ID;
  }

  match(ecommerceConnection: EcommerceConnection ): boolean {
    if ( ecommerceConnection ) {
      return true;
    }
    return false;
  }

  onStoreSelectChange(id: string) {
    const ec = this.ecommerceConnections.find(c => '' + c.ID === id);
    if (ec) {
      this.selectConnection(ec);
    }
  }

  onTypeFilterChange(type: string) {
    this.selectedTypeFilter = type;
    const filtered = this.filteredConnections;
    if (filtered.length > 0 && (!this.selectedEcommerceConnection || filtered.findIndex(c => c.getId() === this.selectedEcommerceConnection.getId()) === -1)) {
      this.selectConnection(filtered[0]);
    }
  }

  selectConnection(ec: EcommerceConnection) {
    this.selectedEcommerceConnection = ec;
    this.selectedEcommerceConnectionId = '' + ec.ID;
    this.editEcommerceConnection = new EcommerceConnection(ec);
  }

  addEcommerceConnection() {
    // Today the only supported ecommerce platform that the SPA can
    // *initiate* an install for is Shopify (server endpoint
    // POST /1.0/ui/ecommerceconnection/shopify/start). Other types
    // (CSV, WooCommerce, etc.) are still added via direct backend
    // configuration. Until that changes, the "Add" affordance is
    // equivalent to "Connect Shopify store".
    this.openConnectShopifyDialog();
  }

  /**
   * Open the Connect Shopify dialog. The dialog itself handles the
   * `shopifyInstallStart` round-trip and the top-level redirect to
   * Shopify's authorize page on success.
   *
   * The server appends `installed=1` and `ecommerceConnectionId`
   * to the `returnTo` URL after callback finalization, which
   * `AppComponent.captureExternalCallbackParams()` recognizes and
   * forwards to this component via a matrix-param route handoff —
   * so we do nothing else here on completion.
   */
  openConnectShopifyDialog(): void {
    const config = modernDialogConfig();
    config.autoFocus = true;
    config.restoreFocus = true;
    config.data = {};
    this.dialog.open(ConnectShopifyDialogComponent, config);
  }

  reactivateEcommerceConnection() {
    console.log('reactivate ecommerce connection');
  }
}
